// components/utils/MQTTMessageMapper.ts

// 1) 타입 축약/복원 매핑 추가 sensor 에서 type:type 오른쪽 type 입니다.
const typeToShort: Record<string, string> = Object.freeze({
  temperature: 'temp',
  humidity: 'humi',
  temp: 'temp',
  humi: 'humi',
  light: 'light',
  voltBat: 'voltBat',
  voltAdc: 'voltAdc',
});

const shortToType: Record<string, string> = Object.freeze({
  temp: 'temperature',
  humi: 'humidity',
  light: 'light',
  voltBat: 'voltBat',
  voltAdc: 'voltAdc',
});


// ✅ 축약 command 코드 매핑
const commandMap = Object.freeze<Record<string, string>>({
  downloadFirmware: 'df',
  setInfo: 'si',
  setOutput: 'so',
  getStatus: 'gs',
  schedule: 'sch',
  bindIO: 'bio',
  bindSensor: 'bs',
  touchInput: 'ti',
  setTime: 'st',
  ottoAction: 'ot',  // Otto 동작 (걷기/굴리기/미끄러짐/정지)
  llmMode: 'lm',     // AI 대화 켜기/끄기 (value 1/0)
  llmReply: 'lr',    // AI 대화 기록 (보드 → 웹: userText, text)
  radio: 'rd',       // 인터넷 라디오 (action: play/stop/vol/get, station, volume)
  ottoLed: 'ol',     // 조명 (effect: sd 켜기/off 끄기/get, color 'rrggbb', brightness 0~255)
  ottoCal: 'oc',     // Otto 보정 (action: get/set/save/clear/p0/p90, calYl/calYr/calTiltR/calTiltL/calSpeedL/calSpeedR)
  apiKey: 'ak'       // Gemini API 키를 보드의 NVS 에 저장/삭제 (key 문자열, 빈 문자열이면 삭제, action 'get' 이면 설정 여부만)
});

// ✅ 축약 operation 코드 매핑
const operationMap = Object.freeze<Record<string, string>>({
  calibration: 'cali',
});

// ✅ 역매핑
const commandReverseMap = Object.freeze<Record<string, string>>(
  Object.entries(commandMap).reduce((acc, [full, short]) => {
    acc[short] = full;
    return acc;
  }, {} as Record<string, string>)
);

const operationReverseMap = Object.freeze<Record<string, string>>(
  Object.entries(operationMap).reduce((acc, [full, short]) => {
    acc[short] = full;
    return acc;
  }, {} as Record<string, string>)
);

// ✅ publish 시: 축약 필드로 압축
export function compressMessage(original: Record<string, any>): Record<string, any> {
  const shortened: Record<string, any> = {};

  // ✅ 입력 메시지 로그 출력
  //console.log('📤 compressMessage 입력 원본:', original);
  //console.log('📤 original.typeSensor:', original.typeSensor);

  if (!original.command) {
    console.warn('❗ compressMessage: command 필드가 없습니다:', original);
  } else if (!commandMap[original.command]) {
    console.warn('❗ compressMessage: 축약 코드에 등록되지 않은 command:', original.command);
  } else {
    shortened.c = commandMap[original.command];
  }

  // sensor에 type:type 오른쪽 type
  if (original.type !== undefined) {
    const t = typeToShort[String(original.type)] || String(original.type);
    shortened.t = t; // 예: humidity -> humi
  }

  // typeSensor를 축약형으로 변환 (typeToShort 사용)
  if (original.typeSensor !== undefined) {
    const typeKey = String(original.typeSensor).toLowerCase();  // typeSensor를 문자열로 변환하여 비교
    const t = typeToShort[typeKey] || typeKey;  // typeToShort에 없으면 원래 값을 사용
    shortened.ts = t;  // 예: "humidity" -> "humi"
  }

  // ✅ 추가된 부분
  if (original.voltAdc !== undefined) shortened.voltAdc = original.voltAdc;
  if (original.voltBat !== undefined) shortened.voltBat = original.voltBat;
  //if (original.adc !== undefined) shortened.adc = original.adc;
  //if (original.bat !== undefined) shortened.bat = original.bat;

  if (original.action !== undefined) shortened.a = original.action;   // Otto 동작 코드
  if (original.userText !== undefined) shortened.us = original.userText; // AI 대화: 사용자가 한 말
  if (original.text !== undefined) shortened.tx = original.text;         // AI 대화: 로봇의 답
  if (original.key !== undefined) shortened.k = original.key;            // API 키 (보드 → 웹으로는 오지 않는다)
  if (original.effect !== undefined) shortened.ef = original.effect;     // 조명: 효과(sd/off/get)
  if (original.color !== undefined) shortened.co = original.color;       // 조명: 색상 'rrggbb'
  if (original.brightness !== undefined) shortened.br = original.brightness; // 조명: 밝기 0~255
  if (original.station !== undefined) shortened.sn = original.station;   // 라디오: 방송국 번호(0부터)
  if (original.volume !== undefined) shortened.vl = original.volume;     // 라디오: 볼륨(0~21)
  if (original.speed !== undefined) shortened.sp = original.speed;       // Otto 동작 속도(%) 40~200, 100이 보통 속도
  if (original.calibration !== undefined) shortened.cali = original.calibration;
  // Otto 보정 (굴림보정/걷기보정)
  if (original.calYl !== undefined) shortened.cyl = original.calYl;           // 왼쪽 다리 수직 펄스폭(us)
  if (original.calYr !== undefined) shortened.cyr = original.calYr;           // 오른쪽 다리
  if (original.calTiltR !== undefined) shortened.ctr = original.calTiltR;     // 걷기: 오른발 기울기
  if (original.calTiltL !== undefined) shortened.ctl = original.calTiltL;     // 걷기: 왼발 기울기
  if (original.calSpeedL !== undefined) shortened.crl = original.calSpeedL;   // 왼발 굴림 속도(us)
  if (original.calSpeedR !== undefined) shortened.crr = original.calSpeedR;   // 오른발 굴림 속도(us)
  if (original.dayOfWeek !== undefined) shortened.dw = original.dayOfWeek;
  if (original.delay !== undefined) shortened.d = original.delay;
  if (original.email) shortened.e = original.email;
  if (original.endMinutes !== undefined) shortened.end = original.endMinutes;
  
  // ✅ 시간 동기화(setTime) 전용 필드들 매핑
  if (original.epoch !== undefined) shortened.epoch = original.epoch;
  if (original.tz_offset !== undefined) shortened.tz_offset = original.tz_offset;

  if (original.fileName !== undefined) shortened.f = original.fileName;
  if (original.humidity !== undefined) shortened.humi = original.humidity;
  if (original.light !== undefined) shortened.light = original.light;
  if (original.mac) shortened.m = original.mac;
  if (original.operation) shortened.o = operationMap[original.operation] || original.operation;
  if (original.pageNo !== undefined) shortened.pn = original.pageNo;
  if (original.pinIndex !== undefined) shortened.pi = original.pinIndex;
  if (original.portNo !== undefined) shortened.n = original.portNo;
  if (original.repeatMode !== undefined) shortened.rm = original.repeatMode;
  if (original.sensorHighValue !== undefined) shortened.h = original.sensorHighValue;
  if (original.sensorLowValue !== undefined) shortened.l = original.sensorLowValue;
  if (original.slotIndex !== undefined) shortened.sI = original.slotIndex;
  if (original.startMinutes !== undefined) shortened.start = original.startMinutes;
  if (original.temperature !== undefined) shortened.temp = original.temperature;
  if (original.trigger !== undefined) shortened.tr = original.trigger === true ? 1 : original.trigger === false ? 0 : original.trigger;
  if (original.triggerValue !== undefined) shortened.tv = original.triggerValue;
  if (original.type !== undefined) shortened.t = original.type;
  if (original.typeSensor !== undefined) shortened.ts = original.typeSensor;
  if (original.value !== undefined) shortened.v = original.value === true ? 1 : original.value === false ? 0 : original.value;

  // ✅ duration 추가
  if (original.duration !== undefined) shortened.du = original.duration;

  if (original.portState) {
    shortened.ps = original.portState.map((item: any) => {
      const compressedItem: any = {};
      if (item.trigger !== undefined) compressedItem.tr = item.trigger === true ? 1 : item.trigger === false ? 0 : item.trigger;
      if (item.mac) compressedItem.m = item.mac;
      if (item.portNo !== undefined) compressedItem.n = item.portNo;
      if (item.delay !== undefined) compressedItem.d = item.delay; // ✅ 이 줄 추가
      if (item.slotIndex !== undefined) compressedItem.sI = item.slotIndex; // ✅ 필요 시 추가
      if (item.value !== undefined) compressedItem.v = item.value === true ? 1 : item.value === false ? 0 : item.value;
      // ✅ portState에도 duration 추가 가능
      if (item.duration !== undefined) compressedItem.du = item.duration;
      return compressedItem;
    });
  }

  //console.log('🧪 compressed message:', shortened);
  return shortened;
}

// ✅ subscribe 시: 축약된 필드를 원래대로 복원
export function decompressMessage(shortened: Record<string, any>): Record<string, any> {
  const expanded: Record<string, any> = {};

  // sensor 의 type
  if (shortened.t !== undefined) {
    const t = shortToType[String(shortened.t)] || String(shortened.t);
    expanded.type = t; // 예: humi -> humidity
  }

  if (shortened.c) expanded.command = commandReverseMap[shortened.c] || shortened.c;
  if (shortened.a !== undefined) expanded.action = shortened.a;   // Otto 동작 코드
  if (shortened.us !== undefined) expanded.userText = shortened.us; // AI 대화: 사용자가 한 말
  if (shortened.tx !== undefined) expanded.text = shortened.tx;     // AI 대화: 로봇의 답
  if (shortened.ok !== undefined) expanded.ok = shortened.ok;       // 명령 처리 결과 (1 성공 / 0 실패)
  if (shortened.err !== undefined) expanded.err = shortened.err;    // 실패 이유
  if (shortened.has !== undefined) expanded.has = shortened.has;    // API 키가 보드에 저장돼 있는지 (1/0). 키 자체는 오지 않는다
  if (shortened.kv !== undefined) expanded.keyValid = shortened.kv; // API 키 확인 결과 (1 정상 / 0 거부 / -1 확인 못 함)
  if (shortened.ef !== undefined) expanded.effect = shortened.ef;   // 조명: 효과
  if (shortened.co !== undefined) expanded.color = shortened.co;    // 조명: 색상
  if (shortened.br !== undefined) expanded.brightness = shortened.br; // 조명: 밝기
  if (shortened.sn !== undefined) expanded.station = shortened.sn;  // 라디오: 방송국 번호
  if (shortened.vl !== undefined) expanded.volume = shortened.vl;   // 라디오: 볼륨
  if (shortened.sp !== undefined) expanded.speed = shortened.sp;    // Otto 동작 속도(%)
  if (shortened.pl !== undefined) expanded.playing = shortened.pl;  // 라디오: 재생 중(1/0)
  if (shortened.cali !== undefined) expanded.calibration = shortened.cali;
  if (shortened.cyl !== undefined) expanded.calYl = shortened.cyl;            // Otto 보정 값 (보드 → 웹)
  if (shortened.cyr !== undefined) expanded.calYr = shortened.cyr;
  if (shortened.ctr !== undefined) expanded.calTiltR = shortened.ctr;
  if (shortened.ctl !== undefined) expanded.calTiltL = shortened.ctl;
  if (shortened.crl !== undefined) expanded.calSpeedL = shortened.crl;
  if (shortened.crr !== undefined) expanded.calSpeedR = shortened.crr;
  if (shortened.csv !== undefined) expanded.calSaved = shortened.csv;         // 보드 NVS 에 저장된 값이 있는지 (1/0)
  if (shortened.d !== undefined) expanded.delay = shortened.d;
  if (shortened.du !== undefined) expanded.duration = shortened.du;
  if (shortened.dw !== undefined) expanded.dayOfWeek = shortened.dw;
  if (shortened.e) expanded.email = shortened.e;
  if (shortened.end !== undefined) expanded.endMinutes = shortened.end;
  
  // ✅ 시간 동기화 필드 복원
  if (shortened.epoch !== undefined) expanded.epoch = shortened.epoch;
  if (shortened.tz_offset !== undefined) expanded.tz_offset = shortened.tz_offset;

  //if (shortened.f !== undefined) expanded.fileName = shortened.f;
  if (shortened.h !== undefined) expanded.sensorHighValue = shortened.h;
  if (shortened.humi !== undefined) expanded.humidity = shortened.humi;
  if (shortened.in !== undefined) expanded.in = shortened.in;
  if (shortened.l !== undefined) expanded.sensorLowValue = shortened.l;
  if (shortened.light !== undefined) expanded.light = shortened.light;
  if (shortened.m) expanded.mac = shortened.m;
  if (shortened.m) { expanded.mac = shortened.m; }
  else if (shortened.from) {
    expanded.mac = shortened.from;   // ✅ m 없으면 from을 mac으로 처리
  }
  if (shortened.n !== undefined) expanded.portNo = shortened.n;
  if (shortened.v !== undefined) expanded.value = shortened.v;
  if (shortened.o) expanded.operation = operationReverseMap[shortened.o] || shortened.o;
  if (shortened.out !== undefined) expanded.out = shortened.out;
  if (shortened.pi !== undefined) expanded.pinIndex = shortened.pi;
  if (shortened.pn !== undefined) expanded.pageNo = shortened.pn;
  if (shortened.rm !== undefined) expanded.repeatMode = shortened.rm;
  if (shortened.si !== undefined) expanded.slotIndex = shortened.si;
  if (shortened.sI !== undefined) expanded.slotIndex = shortened.sI;
  if (shortened.index !== undefined) expanded.slotIndex = shortened.index; // ✅ 보드가 index로 보내는 경우 처리
  // ✅ 만약 보드가 sI를 안 보내고 pi만 보냈는데, 이를 slotIndex로 써야 한다면(사용자 요청):
  if (expanded.slotIndex === undefined && shortened.pi !== undefined) {
    expanded.slotIndex = shortened.pi;
  }
  if (shortened.start !== undefined) expanded.startMinutes = shortened.start;
  if (shortened.t !== undefined) expanded.type = shortened.t;
  if (shortened.ts !== undefined) expanded.typeSensor = shortened.ts;
  if (shortened.temp !== undefined) expanded.temperature = shortened.temp;
  if (shortened.tr !== undefined) expanded.trigger = shortened.tr;
  if (shortened.tv !== undefined) expanded.triggerValue = shortened.tv;
  if (shortened.v !== undefined) expanded.value = shortened.v === 1 ? true : shortened.v === 0 ? false : shortened.v;
  if (shortened.voltAdc !== undefined) expanded.voltAdc = shortened.voltAdc;
  if (shortened.voltBat !== undefined) expanded.voltBat = shortened.voltBat;
  // ✅ gs 응답용 passthrough
  if (shortened.time !== undefined) expanded.time = shortened.time; // 보드 NTP 시간
  if (shortened.tz !== undefined) expanded.tz = shortened.tz;       // 보드 Timezone 시간 
  if (shortened.ver !== undefined) expanded.ver = shortened.ver;  // 펌웨어 버전
  if (shortened.ps) {
    expanded.portState = shortened.ps.map((item: any) => {
      const expandedItem: any = {};
      if (item.tr !== undefined) expandedItem.trigger = item.tr;
      if (item.d !== undefined) expandedItem.delay = item.d; // ✅ 이 줄 추가
      if (item.m) expandedItem.mac = item.m;
      if (item.n !== undefined) expandedItem.portNo = item.n;
      if (item.sI !== undefined) expandedItem.slotIndex = item.sI; // ✅ 필요 시 추가
      if (item.v !== undefined) expandedItem.value = item.v === 1 ? true : item.v === 0 ? false : item.v; // ✅ 고친 부분
      // ✅ portState에도 duration 복원
      if (item.du !== undefined) expandedItem.duration = item.du;
      return expandedItem;
    });
  }

  //console.log('🧩1 Decompressed message:', expanded);
  return expanded;
}
